import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HiMail, HiLockClosed, HiShoppingCart, HiEye, HiEyeOff } from 'react-icons/hi';
import { useAlert } from '../contexts/AlertContext';

export default function Login() {
  const showAlert = useAlert();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const update = (key, value) => setForm({ ...form, [key]: value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (!res.ok) {
        const msg = data.message || 'فشل تسجيل الدخول';
        if (res.status === 403 || msg.toLowerCase().includes('deactivated')) {
          throw new Error('تم حظر هذا الحساب. يرجى التواصل مع المسؤول.');
        }
        throw new Error(msg);
      }

      localStorage.setItem('token', data.tokens.access.token);
      localStorage.setItem('refreshToken', data.tokens.refresh.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      const role = data.user?.role?.name || '';
      navigate(role === 'admin' ? '/' : '/pos');
    } catch (err) {
      showAlert({ type: 'error', title: 'فشل تسجيل الدخول', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary-500 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-gold-400/10 rounded-full blur-3xl animate-warmPulse" />
        <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-gold-500/10 rounded-full blur-3xl animate-warmPulse" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-400/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md animate-fadeIn">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gold-400 rounded-2xl shadow-xl shadow-gold-400/30 mb-4 animate-float">
            <HiShoppingCart className="w-8 h-8 text-primary-500" />
          </div>
          <h1 className="text-3xl font-bold text-white">مرحباً بعودتك</h1>
          <p className="text-primary-200 mt-2">سجل الدخول إلى حسابك في Havana House</p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8">
          {error && (
            <div className="mb-5 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600 font-medium animate-fadeIn">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-primary-500">البريد الإلكتروني</label>
              <div className="relative">
                <HiMail className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-300" />
                <input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  className="w-full pr-12 pl-4 py-3 rounded-xl border border-primary-100 bg-havana-cream/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-primary-500">كلمة المرور</label>
              <div className="relative">
                <HiLockClosed className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-300" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  placeholder="أدخل كلمة المرور"
                  value={form.password}
                  onChange={(e) => update('password', e.target.value)}
                  className="w-full pr-12 pl-12 py-3 rounded-xl border border-primary-100 bg-havana-cream/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-primary-300 hover:text-primary-500 transition-colors"
                >
                  {showPassword ? <HiEyeOff className="w-5 h-5" /> : <HiEye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-primary-200 text-primary-500 focus:ring-primary-500/20" />
                <span className="text-sm text-primary-400">تذكرني</span>
              </label>
              <button type="button" className="text-sm font-semibold text-gold-600 hover:text-gold-500 transition-colors">
                نسيت كلمة المرور؟
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl shadow-lg shadow-primary-500/25 transition-all duration-300 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  جاري تسجيل الدخول...
                </>
              ) : (
                'تسجيل الدخول'
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-primary-100 text-center">
            <p className="text-xs text-primary-300">Havana House — نظام إدارة المبيعات</p>
          </div>
        </div>
      </div>
    </div>
  );
}
